import { Tooltip } from 'antd';
import moment from 'moment';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { DateFieldControlMessageMeta } from 'api/conversations';
import { ProjectId, TaskId } from 'api/models';
import { humanise } from 'components/atoms/humanised-date';
import { defaultParams, getTaskDetailUrl } from 'hooks/use-navigate-to-task';
import { getProjectDetailUrl } from 'utils/urls';
import { AuditWrapper } from './date-change-due-to-dependencies';

const dependencyString = '.DUE_TO_DEPENDENCIES';

export const DateControlMessageDueToNewDependency: FC<{
  meta: DateFieldControlMessageMeta;
  rootKey: string;
}> = ({ meta, rootKey }) => {
  let keyForString = rootKey;
  const { hash } = useLocation();

  if (meta.customField) {
    keyForString += `.${meta.changeType}`;
  } else if (!meta.oldValue) {
    keyForString += `.ADDED${dependencyString}`;
  } else if (!meta.newValue) {
    keyForString += `.REMOVED${dependencyString}`;
  } else {
    keyForString += `.UPDATED${dependencyString}`;
  }

  const projectId =
    meta.dateShiftAuditMeta?.sourceDependentProjectPhase?.project.projectId ??
    meta.dateShiftAuditMeta?.sourceDependentTask?.project?.projectId;

  const sourceUrl = (() => {
    if (
      projectId &&
      meta.dateShiftAuditMeta?.sourceDependentProjectPhase?.project?.projectId
    ) {
      return getProjectDetailUrl(ProjectId(projectId));
    }

    if (meta.dateShiftAuditMeta?.sourceDependentTask?.project?.projectId) {
      return getTaskDetailUrl(
        TaskId(
          meta.dateShiftAuditMeta.sourceDependentTask.project.projectId,
          meta.dateShiftAuditMeta.sourceDependentTask.taskId,
        ),
        defaultParams,
        hash,
      );
    }

    return '';
  })();

  const targetUrl = (() => {
    if (
      projectId &&
      meta.dateShiftAuditMeta?.targetDependentProjectPhase?.project?.projectId
    ) {
      return getProjectDetailUrl(ProjectId(projectId));
    }

    if (meta.dateShiftAuditMeta?.targetDependentTask?.project?.projectId) {
      return getTaskDetailUrl(
        TaskId(
          meta.dateShiftAuditMeta.targetDependentTask.project.projectId,
          meta.dateShiftAuditMeta.targetDependentTask.taskId,
        ),
        defaultParams,
        hash,
      );
    }

    return '';
  })();

  return (
    <AuditWrapper
      isPhase={
        !!meta.dateShiftAuditMeta?.sourceDependentProjectPhase ||
        !!meta.dateShiftAuditMeta?.targetDependentProjectPhase
      }
    >
      <FormattedMessage
        id="CONTROL_MESSAGE.TASK_UPDATE_FIELD.DATE.NEW_DEPENDENCY"
        values={{
          isTask: !!(
            meta.dateShiftAuditMeta?.sourceDependentTask &&
            meta.dateShiftAuditMeta.targetDependentTask
          ),
        }}
      />{' '}
      <Tooltip title={<FormattedMessage id="space_tab.open_in.new_tab" />}>
        <Link to={sourceUrl} target="_blank" rel="noopener noreferrer">
          {meta.dateShiftAuditMeta?.sourceDependentTask
            ? meta.dateShiftAuditMeta?.sourceDependentTask.taskName
            : meta.dateShiftAuditMeta?.sourceDependentProjectPhase
            ? meta.dateShiftAuditMeta?.sourceDependentProjectPhase
                .projectPhaseName
            : ''}
        </Link>{' '}
      </Tooltip>
      <FormattedMessage id="and" />{' '}
      <Tooltip title={<FormattedMessage id="space_tab.open_in.new_tab" />}>
        <Link to={targetUrl} target="_blank" rel="noopener noreferrer">
          {meta.dateShiftAuditMeta?.targetDependentTask
            ? meta.dateShiftAuditMeta.targetDependentTask.taskName
            : meta.dateShiftAuditMeta?.targetDependentProjectPhase
            ? meta.dateShiftAuditMeta?.targetDependentProjectPhase
                .projectPhaseName
            : ''}
        </Link>{' '}
      </Tooltip>
      <FormattedMessage
        id={keyForString}
        values={{
          label: meta.fieldLabel || meta.fieldName,
          from: humanise(moment(meta.oldValue)),
          to: humanise(moment(meta.newValue)),
        }}
      />
    </AuditWrapper>
  );
};
